import { useState, type ReactNode } from 'react';
import { Modal } from './Modal';
import { Button } from '../actions/Button';
import { ReasonField, isReasonValid } from '../forms/ReasonField';

/**
 * Confirmation for a destructive or irreversible action (the kit fires
 * Deactivate and Cancel with none — FE plan §3.7). With `requireReason` the
 * confirm stays disabled until a reason is entered, and `onConfirm` receives
 * it for the audit trail. Mounted by the caller only while open.
 */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  onConfirm,
  onClose,
  requireReason = false,
  busy = false,
}: {
  title: ReactNode;
  body?: ReactNode;
  confirmLabel: string;
  onConfirm: (reason?: string) => void | Promise<void>;
  onClose: () => void;
  requireReason?: boolean;
  busy?: boolean;
}) {
  const [reason, setReason] = useState('');
  const blocked = requireReason && !isReasonValid(reason);
  return (
    <Modal
      isOpen
      onClose={onClose}
      size="md"
      title={title}
      footer={
        <>
          <Button variant="ghost-dark" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={blocked}
            loading={busy}
            onClick={() => void onConfirm(requireReason ? reason.trim() : undefined)}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {body && <div className="mb-4 text-sm text-muted">{body}</div>}
      {requireReason && <ReasonField value={reason} onChange={setReason} />}
    </Modal>
  );
}
